import { useEffect, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, Download, Plus, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { SelectField } from "./ui/select";
import { VisualizationSettings } from "./visualization-settings";
import {
  ReportingWidget,
  ReportDocument,
  downloadResult,
} from "./reporting-renderer";
import { useEngine, useQuery } from "@/lib/query-context";
import { getLocale, tx } from "@/lib/i18n";
import {
  bindingText,
  defaultsFor,
  generateReport,
  mapWidgetInputs,
  type DataBindingDefinition,
  type GeneratedReport,
  type ParameterDefinition,
  type Parameters,
  type ReportTemplateDefinition,
  type ReportingState,
  type WidgetDefinition,
  type WidgetPlacement,
  type TemplateSection,
  type ConditionOperator,
  type InputType,
} from "@/lib/reporting";
import type { QueryResult } from "@/lib/types";

export const editorPanel =
  "rounded-xl border border-border bg-card p-5 space-y-4";
export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5 text-xs font-medium">
      {tx(label)}
      {children}
    </label>
  );
}
export function TextArea(
  props: React.TextareaHTMLAttributes<HTMLTextAreaElement>,
) {
  return (
    <textarea
      {...props}
      className={`min-h-24 w-full rounded-md border border-border bg-card p-3 text-sm ${props.className ?? ""}`}
    />
  );
}
export function ParameterFields({
  definitions,
  values,
  onChange,
}: {
  definitions: ParameterDefinition[];
  values: Parameters;
  onChange: (values: Parameters) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      {definitions.map((p) => (
        <Field key={p.name} label={p.label}>
          {p.type === "boolean" ? (
            <SelectField
              value={String(values[p.name] ?? p.default)}
              onValueChange={(v) =>
                onChange({ ...values, [p.name]: v === "true" })
              }
            >
              <option value="true">{tx("Yes")}</option>
              <option value="false">{tx("No")}</option>
            </SelectField>
          ) : (
            <Input
              aria-label={tx(p.label)}
              type={
                p.type === "date"
                  ? "date"
                  : p.type === "number"
                    ? "number"
                    : "text"
              }
              value={String(values[p.name] ?? p.default)}
              onChange={(e) =>
                onChange({
                  ...values,
                  [p.name]:
                    p.type === "number"
                      ? Number(e.target.value)
                      : e.target.value,
                })
              }
            />
          )}
        </Field>
      ))}
    </div>
  );
}
export function ParameterEditor({
  definitions,
  onChange,
}: {
  definitions: ParameterDefinition[];
  onChange: (p: ParameterDefinition[]) => void;
}) {
  const patch = (i: number, value: Partial<ParameterDefinition>) =>
    onChange(definitions.map((p, j) => (i === j ? { ...p, ...value } : p)));
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">{tx("Parameters")}</h3>
      {definitions.map((p, i) => (
        <div
          key={i}
          className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]"
        >
          <Input
            aria-label={tx("Parameter name")}
            value={p.name}
            onChange={(e) => patch(i, { name: e.target.value })}
          />
          <Input
            aria-label={tx("Parameter label")}
            value={p.label}
            onChange={(e) => patch(i, { label: e.target.value })}
          />
          <SelectField
            aria-label={tx("Parameter type")}
            value={p.type}
            onValueChange={(v) =>
              patch(i, {
                type: v as ParameterDefinition["type"],
                default:
                  v === "boolean"
                    ? false
                    : v === "number"
                      ? 0
                      : v === "date"
                        ? "2026-10-17"
                        : "",
              })
            }
          >
            {["string", "date", "number", "boolean"].map((t) => (
              <option key={t} value={t}>
                {tx(t)}
              </option>
            ))}
          </SelectField>
          {p.type === "boolean" ? (
            <SelectField
              value={String(p.default)}
              aria-label={tx("Default value")}
              onValueChange={(v) => patch(i, { default: v === "true" })}
            >
              <option value="true">{tx("Yes")}</option>
              <option value="false">{tx("No")}</option>
            </SelectField>
          ) : (
            <Input
              aria-label={tx("Default value")}
              type={
                p.type === "date"
                  ? "date"
                  : p.type === "number"
                    ? "number"
                    : "text"
              }
              value={String(p.default)}
              onChange={(e) =>
                patch(i, {
                  default:
                    p.type === "number"
                      ? Number(e.target.value)
                      : e.target.value,
                })
              }
            />
          )}
          <Button
            variant="ghost"
            size="icon"
            aria-label={tx("Remove parameter")}
            onClick={() => onChange(definitions.filter((_, j) => j !== i))}
          >
            <Trash2 />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          onChange([
            ...definitions,
            {
              name: `parameter_${definitions.length + 1}`,
              label: tx("Parameter"),
              type: "string",
              default: "",
            },
          ])
        }
      >
        <Plus />
        {tx("Add parameter")}
      </Button>
    </div>
  );
}
export function MappingEditor({
  widget,
  mapping,
  onChange,
}: {
  widget?: WidgetDefinition;
  mapping: Record<string, string>;
  onChange: (mapping: Record<string, string>) => void;
}) {
  const [key, setKey] = useState("");
  const names = [
    ...new Set([
      ...(widget?.inputs.map((i) => i.name) ?? []),
      ...Object.keys(mapping),
    ]),
  ];
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">{tx("Input mapping")}</h3>
      <p className="text-xs text-muted-foreground">
        {tx(
          "Map Widget input names to query result columns. Use $rows for a generic rows array.",
        )}
      </p>
      {names.map((name) => (
        <Field key={name} label={name}>
          <div className="flex gap-2">
            <Input
              value={mapping[name] ?? name}
              onChange={(e) => onChange({ ...mapping, [name]: e.target.value })}
            />
            <Button
              variant="ghost"
              size="icon"
              aria-label={tx("Remove mapping")}
              onClick={() =>
                onChange(
                  Object.fromEntries(
                    Object.entries(mapping).filter(([k]) => k !== name),
                  ),
                )
              }
            >
              <Trash2 />
            </Button>
          </div>
        </Field>
      ))}
      <div className="flex gap-2">
        <Input
          aria-label={tx("Input name")}
          value={key}
          onChange={(e) => setKey(e.target.value)}
        />
        <Button
          variant="outline"
          onClick={() => {
            if (key.trim()) onChange({ ...mapping, [key.trim()]: key.trim() });
            setKey("");
          }}
        >
          {tx("Add mapping")}
        </Button>
      </div>
    </div>
  );
}
export function BindingSettings({
  binding,
  onChange,
  usage,
}: {
  binding: DataBindingDefinition;
  onChange: (b: DataBindingDefinition) => void;
  usage: string[];
}) {
  return (
    <div className={editorPanel}>
      <p className="text-sm">
        {tx("Shared binding edits affect every listed use.")}{" "}
        {usage.join(", ") || tx("No uses yet")}
      </p>
      <details>
        <summary className="cursor-pointer text-sm font-medium">
          {tx("Parameters and input mapping")}
        </summary>
        <div className="mt-4 space-y-5">
          <ParameterEditor
            definitions={binding.parameters}
            onChange={(parameters) => onChange({ ...binding, parameters })}
          />
          <MappingEditor
            mapping={binding.inputMapping}
            onChange={(inputMapping) => onChange({ ...binding, inputMapping })}
          />
        </div>
      </details>
    </div>
  );
}
export function WidgetEditor({
  seed,
  bindings,
  onSave,
  onBack,
}: {
  seed: WidgetDefinition;
  bindings: DataBindingDefinition[];
  onSave: (widget: WidgetDefinition) => Promise<void>;
  onBack: () => void;
}) {
  const [widget, setWidget] = useState(structuredClone(seed));
  const bindingId =
    bindings.find(
      (b) =>
        b.id === seed.id ||
        b.id === seed.id.replace("legacy-widget-", "legacy-binding-"),
    )?.id ??
    (seed.id === "number" ? "billing-count" : bindings[0]?.id) ??
    "";
  const binding = bindings.find((b) => b.id === bindingId),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  let query: string | undefined;
  try {
    if (binding) query = bindingText(binding);
  } catch {
    /* Query error is shown below. */
  }
  const result = useQuery(query);
  let data: QueryResult | undefined,
    invalid = "";
  try {
    if (result.data && binding)
      data = mapWidgetInputs(widget, result.data, {
        rows: "$rows",
        ...binding.inputMapping,
      });
  } catch (e) {
    invalid = (e as Error).message;
  }
  const schema: QueryResult = data ?? {
    rows: [],
    columns: widget.inputs.map((i) => i.name),
    fields: widget.inputs.map((i) => ({
      ...i,
      label: i.name,
      format: "number",
    })),
    truncated: false,
    elapsedMs: 0,
    diagnostics: [],
  };
  return (
    <div className="space-y-5">
      <div className="flex justify-between gap-3">
        <Button variant="outline" onClick={onBack}>
          {tx("Back")}
        </Button>
        <Button
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError("");
            try {
              if (
                !widget.name.trim() ||
                !widget.inputs.length ||
                new Set(widget.inputs.map((i) => i.name)).size !==
                  widget.inputs.length ||
                widget.inputs.some((i) => !/^[A-Za-z_]\w*$/.test(i.name))
              )
                throw new Error(
                  "Provide a name and a valid, unique input signature.",
                );
              await onSave({ ...widget, revision: seed.revision + 1 });
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {tx("Save Widget")}
        </Button>
      </div>
      {(error || invalid || result.error) && (
        <p
          role="alert"
          className="rounded-lg bg-red-50 p-3 text-sm text-red-800"
        >
          {error || invalid || result.error}
        </p>
      )}
      <div className="grid items-start gap-5 xl:grid-cols-2">
        <div className="space-y-5">
          <section className={editorPanel}>
            <Field label="Name">
              <Input
                value={widget.name}
                onChange={(e) => setWidget({ ...widget, name: e.target.value })}
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={widget.description}
                onChange={(e) =>
                  setWidget({ ...widget, description: e.target.value })
                }
              />
            </Field>
            <h3 className="text-sm font-semibold">
              {tx("Typed input signature")}
            </h3>
            {widget.inputs.map((input, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <Input
                  aria-label={tx("Input name")}
                  className="w-40"
                  value={input.name}
                  onChange={(e) =>
                    setWidget({
                      ...widget,
                      inputs: widget.inputs.map((f, j) =>
                        i === j ? { ...f, name: e.target.value } : f,
                      ),
                    })
                  }
                />
                <SelectField
                  aria-label={tx("Input type")}
                  value={input.type}
                  className="w-36"
                  onValueChange={(value) =>
                    setWidget({
                      ...widget,
                      inputs: widget.inputs.map((f, j) =>
                        i === j ? { ...f, type: value as InputType } : f,
                      ),
                    })
                  }
                >
                  {[
                    "string",
                    "number",
                    "boolean",
                    "date",
                    "timestamp",
                    "array",
                    "record",
                  ].map((t) => (
                    <option value={t} key={t}>
                      {tx(t)}
                    </option>
                  ))}
                </SelectField>
                <label className="flex gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={input.nullable}
                    onChange={(e) =>
                      setWidget({
                        ...widget,
                        inputs: widget.inputs.map((f, j) =>
                          i === j ? { ...f, nullable: e.target.checked } : f,
                        ),
                      })
                    }
                  />
                  {tx("Nullable")}
                </label>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={tx("Remove input")}
                  onClick={() =>
                    setWidget({
                      ...widget,
                      inputs: widget.inputs.filter((_, j) => j !== i),
                    })
                  }
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setWidget({
                  ...widget,
                  inputs: [
                    ...widget.inputs,
                    {
                      name: `input_${widget.inputs.length + 1}`,
                      type: "string",
                      nullable: true,
                    },
                  ],
                })
              }
            >
              <Plus />
              {tx("Add input")}
            </Button>
          </section>
          <section className={editorPanel}>
            <VisualizationSettings
              config={widget.visualization}
              data={schema}
              onChange={(visualization) =>
                setWidget({ ...widget, visualization })
              }
            />
          </section>
        </div>
        <section
          className={`${editorPanel} min-w-0 xl:sticky xl:top-5`}
          aria-label={tx("Widget preview")}
        >
          <h2 className="text-sm font-semibold">{tx("Widget preview")}</h2>
          <div className="max-h-[70vh] overflow-auto rounded-xl border border-border bg-card p-5 space-y-4">
            {widget.visualization.showHeader !== false && (
              <h2 className="text-sm font-semibold">{tx(widget.name)}</h2>
            )}
            {widget.visualization.showDescription !== false &&
              widget.description && (
                <p className="text-xs leading-5 text-muted-foreground">
                  {tx(widget.description)}
                </p>
              )}
            {result.loading && <p role="status">{tx("Loading…")}</p>}
            {!binding && (
              <p className="text-sm text-muted-foreground">
                {tx("Select an existing Widget and data binding.")}
              </p>
            )}
            {data && (
              <ReportingWidget
                widget={widget}
                data={data}
                parameters={binding ? defaultsFor(binding.parameters) : {}}
              />
            )}
            {data &&
              widget.visualization.showFooter !== false &&
              widget.renderer !== "billing-matrix" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadResult(data, widget.name)}
                >
                  <Download />
                  {tx("CSV")}
                </Button>
              )}
          </div>
        </section>
      </div>
    </div>
  );
}
export function PlacementEditor({
  seed,
  state,
  onSave,
  onCancel,
}: {
  seed: WidgetPlacement;
  state: ReportingState;
  onSave: (p: WidgetPlacement) => void;
  onCancel: () => void;
}) {
  const [placement, setPlacement] = useState(structuredClone(seed));
  const widget = state.widgets.find((w) => w.id === placement.widgetId),
    binding = state.bindings.find((b) => b.id === placement.bindingId),
    [error, setError] = useState("");
  let text: string | undefined;
  try {
    if (binding) text = bindingText(binding, placement.parameters);
  } catch (e) {
    text = undefined;
    void e;
  }
  const result = useQuery(text);
  let data: QueryResult | undefined,
    invalid = "";
  try {
    if (widget && binding && result.data)
      data = mapWidgetInputs(widget, result.data, {
        ...binding.inputMapping,
        ...placement.inputMapping,
      });
  } catch (e) {
    invalid = (e as Error).message;
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Widget">
          <SelectField
            value={placement.widgetId}
            onValueChange={(widgetId) =>
              setPlacement({ ...placement, widgetId, visualization: undefined })
            }
          >
            {state.widgets.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </SelectField>
        </Field>
        <Field label="Data binding">
          <SelectField
            value={placement.bindingId}
            onValueChange={(bindingId) =>
              setPlacement({ ...placement, bindingId })
            }
          >
            {state.bindings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </SelectField>
        </Field>
        <Field label="Local title">
          <Input
            value={placement.title ?? ""}
            onChange={(e) =>
              setPlacement({ ...placement, title: e.target.value })
            }
          />
        </Field>
        <Field label="Width">
          <SelectField
            value={placement.width}
            onValueChange={(width) =>
              setPlacement({
                ...placement,
                width: width as WidgetPlacement["width"],
              })
            }
          >
            {["fifth", "third", "half", "full"].map((w) => (
              <option key={w} value={w}>
                {tx(w)}
              </option>
            ))}
          </SelectField>
        </Field>
      </div>
      <Field label="Local description">
        <TextArea
          value={placement.description ?? ""}
          onChange={(e) =>
            setPlacement({ ...placement, description: e.target.value })
          }
        />
      </Field>
      {binding && (
        <ParameterFields
          definitions={binding.parameters}
          values={placement.parameters ?? defaultsFor(binding.parameters)}
          onChange={(parameters) => setPlacement({ ...placement, parameters })}
        />
      )}
      <MappingEditor
        widget={widget}
        mapping={{ ...binding?.inputMapping, ...placement.inputMapping }}
        onChange={(inputMapping) =>
          setPlacement({ ...placement, inputMapping })
        }
      />
      {widget && data && (
        <>
          <details>
            <summary className="cursor-pointer text-sm">
              {tx("Placement presentation")}
            </summary>
            <div className="mt-4">
              <VisualizationSettings
                config={{ ...widget.visualization, ...placement.visualization }}
                data={data}
                onChange={(visualization) =>
                  setPlacement({ ...placement, visualization })
                }
              />
            </div>
          </details>
          <ReportingWidget
            widget={{
              ...widget,
              visualization: {
                ...widget.visualization,
                ...placement.visualization,
              },
            }}
            data={data}
            parameters={
              placement.parameters ?? defaultsFor(binding?.parameters ?? [])
            }
          />
        </>
      )}
      {(invalid || error || result.error) && (
        <p role="alert" className="text-sm text-red-700">
          {invalid || error || result.error}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          variant="ghost"
          onClick={() =>
            setPlacement({
              ...placement,
              visualization: undefined,
              title: undefined,
              description: undefined,
              inputMapping: undefined,
            })
          }
        >
          {tx("Reset to Widget defaults")}
        </Button>
        <Button variant="outline" onClick={onCancel}>
          {tx("Cancel")}
        </Button>
        <Button
          disabled={!data || result.loading || !!invalid}
          onClick={() => {
            if (!widget || !binding) {
              setError("Select a Widget and data binding.");
              return;
            }
            onSave(placement);
          }}
        >
          {tx("Save placement")}
        </Button>
      </div>
    </div>
  );
}
export function TemplateEditor({
  seed,
  state,
  onSave,
  onBack,
}: {
  seed: ReportTemplateDefinition;
  state: ReportingState;
  onSave: (t: ReportTemplateDefinition) => void;
  onBack: () => void;
}) {
  const [template, setTemplate] = useState(structuredClone(seed)),
    [preview, setPreview] = useState<GeneratedReport>(),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const engine = useEngine();
  useEffect(() => {
    setPreview(undefined);
  }, [template]);
  const patchSection = (index: number, patch: Partial<TemplateSection>) => {
    setPreview(undefined);
    setTemplate({
      ...template,
      sections: template.sections.map((s, i) =>
        i === index ? ({ ...s, ...patch } as TemplateSection) : s,
      ),
    });
  };
  const move = (index: number, direction: number) => {
    const sections = [...template.sections],
      other = index + direction;
    if (other < 0 || other >= sections.length) return;
    [sections[index], sections[other]] = [sections[other], sections[index]];
    setTemplate({ ...template, sections });
    setPreview(undefined);
  };
  const validate = async () => {
    if (!template.name.trim() || !template.sections.length)
      throw new Error(
        "A Report Template needs a name and at least one section.",
      );
    if (
      new Set(template.parameters.map((p) => p.name)).size !==
        template.parameters.length ||
      template.parameters.some((p) => !/^\w+$/.test(p.name))
    )
      throw new Error("Parameter names must be valid and unique.");
    return generateReport(
      {
        ...state,
        templates: state.templates.map((t) =>
          t.id === template.id ? template : t,
        ),
      },
      template.id,
      defaultsFor(template.parameters),
      getLocale(),
      engine.query,
    );
  };
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="outline" onClick={onBack}>
          {tx("Back")}
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={busy}
            onClick={async () => {
              setError("");
              setBusy(true);
              try {
                setPreview(await validate());
              } catch (e) {
                setError((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {tx("Preview template")}
          </Button>
          <Button
            disabled={busy}
            onClick={async () => {
              setError("");
              setBusy(true);
              try {
                await validate();
                onSave({ ...template, revision: seed.revision + 1 });
              } catch (e) {
                setError((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {tx("Save Report Template")}
          </Button>
        </div>
      </div>
      {error && (
        <p
          role="alert"
          className="rounded-lg bg-red-50 p-3 text-sm text-red-800"
        >
          {error}
        </p>
      )}
      <section className={editorPanel}>
        <Field label="Property">
          <Input
            value={template.propertyName ?? ""}
            onChange={(e) =>
              setTemplate({ ...template, propertyName: e.target.value })
            }
          />
        </Field>
        <Field label="Source as of">
          <Input
            value={template.sourceAt}
            onChange={(e) =>
              setTemplate({ ...template, sourceAt: e.target.value })
            }
          />
        </Field>
        <Field label="Name">
          <Input
            value={template.name}
            onChange={(e) => {
              setPreview(undefined);
              setTemplate({ ...template, name: e.target.value });
            }}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={template.description}
            onChange={(e) => {
              setPreview(undefined);
              setTemplate({ ...template, description: e.target.value });
            }}
          />
        </Field>
        <ParameterEditor
          definitions={template.parameters}
          onChange={(parameters) => {
            setPreview(undefined);
            setTemplate({ ...template, parameters });
          }}
        />
        <details>
          <summary className="cursor-pointer text-sm font-medium">
            {tx("Named data bindings")}
          </summary>
          <p className="my-2 text-xs text-muted-foreground">
            {tx(
              "Widget and condition bindings are included automatically. Add bindings used by text placeholders here.",
            )}
          </p>
          {state.bindings.map((b) => (
            <label className="my-2 flex items-center gap-2 text-xs" key={b.id}>
              <input
                type="checkbox"
                checked={template.bindingIds.includes(b.id)}
                onChange={(e) =>
                  setTemplate({
                    ...template,
                    bindingIds: e.target.checked
                      ? [...template.bindingIds, b.id]
                      : template.bindingIds.filter((id) => id !== b.id),
                  })
                }
              />
              {b.name} · <code>{b.id}</code>
            </label>
          ))}
        </details>
      </section>
      <p className="text-xs text-muted-foreground">
        {tx(
          "Text placeholders: {{params.name}} or {{binding-id.field}}. Scalar placeholders require one result row.",
        )}
      </p>
      {template.sections.map((section, i) => (
        <section className={editorPanel} key={section.id}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">
              {i + 1}.{" "}
              {tx(section.kind === "text" ? "Text section" : "Widget section")}
            </h3>
            <div className="flex">
              <Button
                variant="ghost"
                size="icon"
                disabled={i === 0}
                aria-label={tx("Move up")}
                onClick={() => move(i, -1)}
              >
                <ArrowUp />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                disabled={i === template.sections.length - 1}
                aria-label={tx("Move down")}
                onClick={() => move(i, 1)}
              >
                <ArrowDown />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={tx("Remove section")}
                onClick={() =>
                  setTemplate({
                    ...template,
                    sections: template.sections.filter((_, j) => j !== i),
                  })
                }
              >
                <Trash2 />
              </Button>
            </div>
          </div>
          <Field label="Section title">
            <Input
              value={section.title}
              onChange={(e) => patchSection(i, { title: e.target.value })}
            />
          </Field>
          {section.kind === "text" ? (
            <>
              <Field label="Text">
                <TextArea
                  value={section.text}
                  onChange={(e) => patchSection(i, { text: e.target.value })}
                />
              </Field>
              <Field label="Text columns">
                <SelectField
                  value={String(section.columns ?? 1)}
                  onValueChange={(value) =>
                    patchSection(i, { columns: Number(value) as 1 | 2 })
                  }
                >
                  <option value="1">{1}</option>
                  <option value="2">{2}</option>
                </SelectField>
              </Field>
            </>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Widget">
                <SelectField
                  value={section.widgetId}
                  onValueChange={(widgetId) => patchSection(i, { widgetId })}
                >
                  {state.widgets.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </SelectField>
              </Field>
              <Field label="Data binding">
                <SelectField
                  value={section.bindingId}
                  onValueChange={(bindingId) => patchSection(i, { bindingId })}
                >
                  {state.bindings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </SelectField>
              </Field>
            </div>
          )}
          <label className="flex gap-2 text-xs">
            <input
              type="checkbox"
              checked={!!section.condition}
              onChange={(e) =>
                patchSection(i, {
                  condition: e.target.checked
                    ? {
                        bindingId: state.bindings[0]?.id ?? "",
                        operator: "hasRows",
                      }
                    : undefined,
                })
              }
            />
            {tx("Conditional visibility")}
          </label>
          {section.condition && (
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label="Condition binding">
                <SelectField
                  value={section.condition.bindingId}
                  onValueChange={(bindingId) =>
                    patchSection(i, {
                      condition: { ...section.condition!, bindingId },
                    })
                  }
                >
                  {state.bindings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </SelectField>
              </Field>
              <Field label="Condition operator">
                <SelectField
                  value={section.condition.operator}
                  onValueChange={(operator) =>
                    patchSection(i, {
                      condition: {
                        ...section.condition!,
                        operator: operator as ConditionOperator,
                      },
                    })
                  }
                >
                  {[
                    "hasRows",
                    "noRows",
                    "eq",
                    "ne",
                    "gt",
                    "gte",
                    "lt",
                    "lte",
                    "isNull",
                    "notNull",
                  ].map((op) => (
                    <option value={op} key={op}>
                      {tx(op)}
                    </option>
                  ))}
                </SelectField>
              </Field>
              {!["hasRows", "noRows"].includes(section.condition.operator) && (
                <Field label="Condition field">
                  <Input
                    value={section.condition.field ?? ""}
                    onChange={(e) =>
                      patchSection(i, {
                        condition: {
                          ...section.condition!,
                          field: e.target.value,
                        },
                      })
                    }
                  />
                </Field>
              )}
              {!["hasRows", "noRows", "isNull", "notNull"].includes(
                section.condition.operator,
              ) && (
                <>
                  <Field label="Comparison type">
                    <SelectField
                      value={typeof (section.condition.value ?? "")}
                      onValueChange={(v) =>
                        patchSection(i, {
                          condition: {
                            ...section.condition!,
                            value:
                              v === "number" ? 0 : v === "boolean" ? false : "",
                          },
                        })
                      }
                    >
                      {["string", "number", "boolean"].map((t) => (
                        <option key={t} value={t}>
                          {tx(t)}
                        </option>
                      ))}
                    </SelectField>
                  </Field>
                  <Field label="Comparison value">
                    <Input
                      value={String(section.condition.value ?? "")}
                      onChange={(e) =>
                        patchSection(i, {
                          condition: {
                            ...section.condition!,
                            value:
                              typeof section.condition!.value === "number"
                                ? Number(e.target.value)
                                : typeof section.condition!.value === "boolean"
                                  ? e.target.value === "true"
                                  : e.target.value,
                          },
                        })
                      }
                    />
                  </Field>
                </>
              )}
            </div>
          )}
        </section>
      ))}
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() =>
            setTemplate({
              ...template,
              sections: [
                ...template.sections,
                {
                  id: crypto.randomUUID(),
                  title: tx("Text section"),
                  kind: "text",
                  text: "",
                },
              ],
            })
          }
        >
          <Plus />
          {tx("Add text section")}
        </Button>
        <Button
          variant="outline"
          disabled={!state.widgets.length || !state.bindings.length}
          onClick={() =>
            setTemplate({
              ...template,
              sections: [
                ...template.sections,
                {
                  id: crypto.randomUUID(),
                  title: tx("Widget section"),
                  kind: "widget",
                  widgetId: state.widgets[0].id,
                  bindingId: state.bindings[0].id,
                },
              ],
            })
          }
        >
          <Plus />
          {tx("Add Widget section")}
        </Button>
      </div>
      {busy && <p role="status">{tx("Preparing preview…")}</p>}
      {preview && <ReportDocument report={preview} />}
    </div>
  );
}
